import { PROCESSED_NOTIFICATION_KINDS } from './loanRequest';
import type { ValueOf } from './util/typeUtils';

/**
 * Shared constants for WebSocket transport and wire protocol.
 *
 * The backend exposes authenticated WebSocket routes; the shared multiplexed `/ws` endpoint
 * dispatches client → server messages by `type` (one of `WS_MESSAGE_TYPE`). Server → client pushes
 * use the same top-level `type` so the frontend `wsClient` can dispatch to the matching handler.
 * Chat thread sockets (`/ws/chat/:chatId`) and user-notify pushes (inbox, notification bell) share the close codes and
 * socket-kind registry below. New realtime features plug in by adding a `WS_MESSAGE_TYPE` value and
 * a handler — the transport and auth stay shared.
 */

/** Mirrors `ws` / WHATWG `WebSocket` ready states — avoid importing `ws` (no types on ws@5). */
export const WS_READY_STATE = {
    CLOSED: 3,
    CLOSING: 2,
    CONNECTING: 0,
    OPEN: 1,
} as const;

/** Path of the shared WebSocket endpoint. */
export const WS_PATH = '/ws';

export const WS_MESSAGE_TYPE = {
    /** WhatsApp chat unread invalidation pushes (server → client). */
    CHAT_INBOX: 'chatInbox',
    EVENT_CHAIN: 'eventChain',
    /** Notification bell invalidation pushes (server → client). */
    NOTIFICATIONS: 'notifications',
} as const;

export type WsMessageType = ValueOf<typeof WS_MESSAGE_TYPE>;

/**
 * Application close codes (must be in the 4000–4999 private range).
 *
 * Each code selects a branch in the frontend `wsClient`'s reconnect logic, so the consequence of the
 * one you pick lives in another package:
 *
 * - `FORBIDDEN` — a real denial (inactive user / ACL revoke): client stops reconnecting, drops its ACL cache.
 * - `SERVICE_UNAVAILABLE` — the server could not *determine* access (no Blackrik instance, read-model
 *   timeout): client reconnects with backoff. Never use it for a denial; it leaves the ACL cache stale.
 * - `UNAUTHENTICATED` / `TOKEN_EXPIRED` — cookie missing/expired or JWT `exp` hit: renew, then reconnect.
 * - `UNAUTHORIZED` — not produced by the backend; kept for the client's defensive stop-reconnect branch.
 */
export const WS_CLOSE_CODE = {
    FORBIDDEN: 4003,
    SERVICE_UNAVAILABLE: 4004,
    TOKEN_EXPIRED: 4008,
    UNAUTHENTICATED: 4001,
    UNAUTHORIZED: 4401,
} as const;

export type WsCloseCode = ValueOf<typeof WS_CLOSE_CODE>;

/** Why an acl-revoke was published; `ROLE_CHANGED` closes every socket of the user (they hold the old role). */
export const WS_ACL_REVOKE_REASONS = {
    ROLE_CHANGED: 'role-changed',
} as const;

export type WsAclRevokeReason = ValueOf<typeof WS_ACL_REVOKE_REASONS>;

/** Discriminator for sockets registered in cpool-backend's `socketMeta` registry
 *  (`/ws/chat/:chatId` vs shared `/ws`). */
export const WS_SOCKET_KINDS = {
    CHAT_THREAD: 'chat-thread',
    /** Shared multiplexed `/ws` socket — receives user-notify pushes (chat inbox, notification bell). */
    GENERAL: 'general',
} as const;

export type WS_SOCKET_KIND = ValueOf<typeof WS_SOCKET_KINDS>;

/**
 * Wire `event` values for pushes framed as `{ type: WS_MESSAGE_TYPE.CHAT_INBOX, event }` on
 * `ws:user-notify:{userId}` → shared `/ws` GENERAL sockets.
 */
export const WS_INBOX_MESSAGE_TYPES = {
    INVALIDATE_UNREAD: 'invalidate-unread',
} as const;

export type WS_INBOX_MESSAGE_TYPE = ValueOf<typeof WS_INBOX_MESSAGE_TYPES>;

// `kind` of `{ type: NOTIFICATIONS, kind, position }` pushes on `ws:user-notify:{userId}`
export const WS_NOTIFICATION_KINDS = {
    ...PROCESSED_NOTIFICATION_KINDS,
    PROCESSED: 'PROCESSED',
} as const;

export type WsNotificationKind = ValueOf<typeof WS_NOTIFICATION_KINDS>;

/** Wire `type` values pushed on chat thread channels (`chat:thread:{chatId}` → `/ws/chat/:chatId`). */
export const WS_CHAT_MESSAGE_TYPES = {
    MESSAGE: 'message',
    /** Server-only ACL re-verification signal — never forwarded to clients (see
     *  `notifyChatRecheck`/`handleChatChannelMessage`). */
    RECHECK: 'recheck',
} as const;

export type WS_CHAT_MESSAGE_TYPE = ValueOf<typeof WS_CHAT_MESSAGE_TYPES>;
